import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  TitleStrategy,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideMonacoEditor } from 'ngx-monaco-editor-v2';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService } from 'primeng/api';
import { SEVERITY_AWARE_MESSAGE_SERVICE } from './shared/services/toast/severity-aware-message.service';
import { PRIMENG_CONFIG } from './core/config/primeng-config';
import {
  MonacoJsonLanguages,
  MonacoUiConfigSchemaService,
} from './core/config/monaco-ui-config-schema.service';
import { ChameleonService } from './core/chameleon/chameleon.service';
import { ThemeService } from './core/theme/theme.service';
import { FeatureFlagService } from './core/feature-flags/feature-flag.service';
import { FormatCellPipe } from './shared/pipes/format-cell.pipe';
import { provideNumberLocaleFromLocaleId } from './shared/services/formatters/number-locale.provider';
import { HeatmapColorPipe } from './shared/pipes/heatmap-color.pipe';

import { routes } from './app.routes';
import { authTokenInterceptor } from './core/http/auth-token.interceptor';
import { datasetVersionInterceptor } from './core/http/dataset-version.interceptor';
import { accessDeniedRefreshInterceptor } from './core/http/access-denied-refresh.interceptor';
import { requestQueueInterceptor } from './core/http/request-queue.interceptor';
import { handleChunkLoadNavigationError } from './core/router/chunk-load-navigation-error.handler';
import { ConstantTitleStrategy } from './core/router/constant-title.strategy';
import { provideInAppHistory } from './core/router/in-app-history.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      // Preserve scroll position across navigation (e.g. drill-into-detail and
      // back). Anchor scrolling is also enabled so in-page #anchors work.
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      // GF-4700: a tab left open across a deploy still holds the previous
      // build's index.html, so navigating to a not-yet-loaded lazy route 404s
      // on that chunk and the router silently swallows the failure. Recover
      // by forcing the attempted URL to load fresh when that happens.
      withNavigationErrorHandler(handleChunkLoadNavigationError)
    ),
    // Pins the tab title to the product name on every navigation, overriding
    // Angular's root DefaultTitleStrategy (which pushes per-route `title` and
    // leaves a stale value on untitled routes). Routes carry no `title`; this pins the tab title.
    { provide: TitleStrategy, useClass: ConstantTitleStrategy },
    // GF-6405: a drill-in page's Back returns to the page the user actually came from.
    provideInAppHistory(),
    // Angular 20.2 deprecated `provideAnimationsAsync()` and PrimeNG 21
    // dropped its dependency on the `@angular/animations` package in
    // favor of native CSS-based animations, so no animations provider
    // is wired here. Components like Dialog / OverlayPanel / Tooltip
    // / Menubar continue to animate via PrimeNG's CSS keyframes.
    provideHttpClient(
      withInterceptors([
        requestQueueInterceptor,
        authTokenInterceptor,
        accessDeniedRefreshInterceptor,
        datasetVersionInterceptor,
      ])
    ),
    providePrimeNG(PRIMENG_CONFIG),
    /**
     * Toast policy. Errors stay until closed unless the caller sets `life` or `sticky`; success and
     * info close after 3 s, warn after 5 s. Data conditions never toast: they render inline on the
     * page. Each page has one keyless host, the scaffold's; a keyed host only for a custom template
     * or a portaled surface (the shell's export notices, the chat drawer). Every host lands top-right
     * below the top bar (TOAST_PLACEMENT); inside the shell they stack in one column (ToastLane).
     */
    SEVERITY_AWARE_MESSAGE_SERVICE,
    ConfirmationService,
    // Root-provided so any component can `inject(FormatCellPipe)` (e.g.
    // createCmiInsightsSummary()) without each one repeating its own
    // `providers: [FormatCellPipe]` — it's stateless (wraps the already
    // root-provided ValueFormatterService + LOCALE_ID), so one instance is
    // safe app-wide. Components still list it in `imports` separately to
    // use it as a template `| formatCell` pipe; that's a distinct concern
    // from DI injection.
    FormatCellPipe,
    // Root-provided so any component can `inject(HeatmapColorPipe)` (e.g.
    // Adherence's rep-grid `cellColorStrategy`, GF-4748) without each one
    // repeating its own `providers: [HeatmapColorPipe]` — it's stateless
    // (wraps the already root-provided ThemeService), so one instance is
    // safe app-wide. Components still list it in `imports` separately to
    // use it as a template `| heatmapColor` pipe; that's a distinct concern
    // from DI injection.
    HeatmapColorPipe,
    provideAppInitializer(() => inject(ThemeService).init()),
    // ``FeatureFlagService.beginBoot()`` kicks off Amplitude bootstrap
    // end-to-end (``amplitude.init()`` → SDK download → ``initAll`` →
    // anonymous variant fetch). We deliberately ``void`` it rather than
    // return the promise: ``provideAppInitializer`` AWAITS any promise the
    // callback returns, so returning ``beginBoot()`` would block first paint
    // on the ~241 KB Amplitude SDK download + variant fetch — the very thing
    // GF-4386 removes. Firing it and discarding the promise starts the boot
    // without holding up bootstrap. The /login + /login/complete routes have
    // no flag guards, so they paint immediately. The auth-gated, flag-gated
    // routes each ``await flags.ready()`` in their guard, which awaits this
    // same in-flight boot (memoized in ``bootPromise``) — so gated guards
    // still resolve flags correctly on a cold boot, just without holding up
    // the very first paint. (``beginBoot``'s promise never rejects —
    // ``amplitude.init`` and ``whenVariantsReady`` both swallow errors — so
    // the ``void`` cannot leak an unhandled rejection.)
    //
    // ``FeatureFlagService`` owns Amplitude bootstrap as the single entry
    // point: ``AmplitudeService.init()`` is idempotent (``initPromise``
    // guard), so we INTENTIONALLY do not register a separate
    // ``AmplitudeService.init()`` initializer that could race past that
    // guard and run ``sdk.initAll(...)`` twice.
    //
    // ``provideAppInitializer()`` (Angular 19+) supersedes the deprecated
    // ``APP_INITIALIZER`` token: the callback runs inside an injection
    // context so the manual ``useFactory`` / ``deps`` indirection
    // collapses into a single ``inject(...)`` call per initializer.
    // Makes `LOCALE_ID` the single source for every rendered number, not just
    // for dates. See the provider for why the formatting layer needs it copied
    // rather than injected.
    provideNumberLocaleFromLocaleId(),
    provideAppInitializer(() => {
      void inject(FeatureFlagService).beginBoot();
    }),
    // Chameleon kicks off after first paint too; ``init()`` is a no-op when
    // disabled and only dynamically imports the 192 KB SDK once the
    // ``environment.chameleon.enabled`` guard passes.
    provideAppInitializer(() => {
      void inject(ChameleonService).init();
    }),
    // ``onMonacoLoad`` below fires from inside the AMD loader's async
    // ``require`` callback (see ``ngx-monaco-editor-v2``'s ``EditorComponent``),
    // which is NOT an injection context — ``inject()`` there throws NG0203.
    // ``provideAppInitializer`` runs inside one, so we resolve the service
    // here and have ``onMonacoLoad`` use the already-resolved instance instead
    // (needed once that callback started injecting ``HttpClient``).
    provideAppInitializer(() => {
      monacoSchemaService = inject(MonacoUiConfigSchemaService);
    }),
    // Monaco editor for the tenant UI config admin page (GF-3909). Loads
    // the AMD distribution from ``assets/monaco/min/vs`` (copied at build
    // time per angular.json) and registers the canonical UIConfig JSON
    // Schema against every JSON model in the app — there's only one JSON
    // editor today, so nothing is scoped further; see
    // ``MonacoUiConfigSchemaService`` for the ``fileMatch`` caveat if a
    // second JSON editor is ever added — so edits in the admin editor get
    // autocomplete + inline error markers.
    provideMonacoEditor({
      baseUrl: 'assets/monaco/min/vs',
      defaultOptions: {
        scrollBeyondLastLine: false,
        minimap: { enabled: false },
        formatOnPaste: true,
        formatOnType: false,
        tabSize: 2,
        automaticLayout: true,
      },
      onMonacoLoad: () => {
        // ``window.monaco`` is the global Monaco namespace once the AMD
        // loader resolves.
        const monaco = (window as unknown as { monaco?: { languages: MonacoJsonLanguages } })
          .monaco;
        if (!monaco || !monacoSchemaService) return;
        // the schema now comes live from the API (``GET
        // /ui-config/schema``) via ``HttpClient``, so the auth interceptor
        // attaches a bearer token — a raw ``fetch()`` would bypass it. This
        // fires once, only when an admin opens /admin/configs, so it costs
        // cold boot nothing.
        monacoSchemaService.register(monaco.languages);
      },
    }),
  ],
};

// Set by the ``provideAppInitializer`` above once the app finishes bootstrapping;
// ``onMonacoLoad`` reads it instead of calling ``inject()`` directly. See the
// comment on that initializer for why.
let monacoSchemaService: MonacoUiConfigSchemaService | null = null;
